import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const translations = {
  en: {
    nav: {
      howItWorks: 'How it Works',
      whyWatrly: 'Why Watrly',
      platform: 'Platform',
      requestWater: 'Request Water',
    },
    hero: {
      badge: 'Water Infrastructure Platform',
      title: 'Water delivery,',
      titleHighlight: 'engineered',
      titleEnd: 'for Saudi Arabia',
      subtitle: 'Connect with licensed water stations. Track every tanker. From 18 to 32 tons, delivered with precision and full accountability.',
      cta: 'Request Water',
      ctaSecondary: 'See How It Works',
      statCapacity: 'Ton Capacity',
      statTracking: 'GPS Tracking',
      statLive: 'Live',
      statOperations: 'Operations',
      scroll: 'Scroll',
    },
    problem: {
      badge: 'The Challenge',
      title: 'Water tanker logistics in Saudi Arabia remains fragmented',
      subtitle: 'The industry operates on outdated systems that create friction for everyone involved — from customers seeking reliable delivery to station owners managing complex operations.',
      problems: [
        {
          title: 'Manual Coordination',
          description: 'Hours spent calling multiple stations, comparing prices, and arranging delivery times through fragmented phone calls.',
        },
        {
          title: 'No Real-Time Tracking',
          description: 'Customers left waiting with no visibility on tanker location, estimated arrival, or delivery status updates.',
        },
        {
          title: 'Unclear Pricing',
          description: 'Inconsistent rates across stations with no transparent pricing structure, leading to unexpected costs and disputes.',
        },
        {
          title: 'Operational Chaos',
          description: 'Station owners struggle to manage driver allocation, track fleet utilization, and maintain service quality at scale.',
        },
      ],
      bottomBadge: 'Watrly was built to solve these exact problems',
    },
    howItWorks: {
      badge: 'How It Works',
      title: 'From request to delivery in four steps',
      subtitle: 'A streamlined process designed for clarity and reliability. Every order follows a precise operational flow.',
      steps: [
        {
          number: '01',
          title: 'Choose Tanker Size',
          description: 'Select the capacity you need. 18-ton for residential, 32-ton for commercial and industrial sites.',
        },
        {
          number: '02',
          title: 'Nearest Station Located',
          description: 'Our system automatically identifies the closest water station with available capacity and assigns your order.',
        },
        {
          number: '03',
          title: 'Driver Assigned',
          description: 'A verified driver is dispatched from the station. You receive their details and vehicle information instantly.',
        },
        {
          number: '04',
          title: 'Live Tracking',
          description: 'Track your tanker in real-time from station to your location. Status updates at every stage of delivery.',
        },
      ],
    },
    whyWatrly: {
      badge: 'Why Watrly',
      title: 'Infrastructure built for reliability',
      subtitle: 'Every feature exists to solve a real operational challenge. No unnecessary complexity, no marketing gimmicks — just the tools needed to run water logistics efficiently.',
      stationNetwork: 'Watrly Station Network',
      stationDesc: 'Licensed water stations across Saudi Arabia',
      features: [
        {
          title: 'Real-Time GPS Tracking',
          description: 'Monitor every tanker from station departure to customer delivery with live location updates.',
        },
        {
          title: 'Station-Based Dispatch',
          description: 'Orders automatically route to the nearest licensed water station with available capacity.',
        },
        {
          title: 'Driver Accountability',
          description: 'Verified drivers with performance metrics, delivery history, and customer ratings.',
        },
        {
          title: 'Admin Control Center',
          description: 'Complete operational oversight with order management, fleet tracking, and analytics dashboard.',
        },
        {
          title: 'Flexible Payment',
          description: 'Digital payment via Apple Pay and online methods, or traditional cash on delivery.',
        },
        {
          title: 'Enterprise Ready',
          description: 'API integrations, bulk ordering, recurring deliveries, and dedicated account management.',
        },
      ],
    },
    platform: {
      badge: 'Platform',
      title: 'Designed for every user',
      subtitle: 'Purpose-built interfaces for customers, drivers, and administrators. Each experience optimized for its specific operational needs.',
      tabs: {
        customer: 'Customer App',
        admin: 'Admin Dashboard',
        tracking: 'Live Tracking',
      },
      customer: {
        title: 'Order water in seconds',
        description: 'A streamlined mobile experience for requesting water delivery, tracking orders in real-time, and managing payment — all from your phone.',
        features: ['Select tanker capacity', 'Automatic station matching', 'Live GPS tracking', 'Digital payment options'],
      },
      admin: {
        title: 'Complete operational control',
        description: 'A powerful control center for station owners and administrators to manage orders, monitor drivers, and track fleet performance.',
        features: ['Order management', 'Driver assignment', 'Fleet tracking', 'Performance analytics'],
      },
      tracking: {
        title: 'Visibility at every step',
        description: 'Real-time status updates from order placement through delivery completion. Know exactly where your water is at all times.',
        features: ['GPS location tracking', 'Status notifications', 'ETA updates', 'Delivery confirmation'],
      },
      mockup: {
        welcomeBack: 'Welcome back',
        activeDelivery: 'Active Delivery',
        tonTanker: 'Ton Tanker',
        enRoute: 'En route',
        minAway: 'min away',
        selectCapacity: 'Select Capacity',
        ton: 'Ton',
        deliveryLocation: 'Delivery Location',
        mapPreview: 'Map Preview',
        operationsDashboard: 'Operations Dashboard',
        realtimeOverview: 'Real-time overview',
        live: 'Live',
        onTimeRate: 'On-Time Rate',
        driversOnline: 'Drivers Online',
        activeOrders: 'Active Orders',
        recentOrders: 'Recent Orders',
        delivering: 'Delivering',
        filling: 'Filling',
        assigned: 'Assigned',
        min: 'min',
        deliveryStatus: 'Delivery Status',
        onTheWay: 'On The Way',
        orderPlaced: 'Order Placed',
        driverAssigned: 'Driver Assigned',
        fillingComplete: 'Filling Complete',
      },
    },
    scale: {
      badge: 'Built for Scale',
      title: 'Infrastructure that grows with Saudi Arabia',
      subtitle: 'Watrly is not a temporary solution. It is a long-term infrastructure investment designed to support the Kingdom\'s growth — from individual deliveries to large-scale municipal and industrial operations.',
      capabilities: [
        {
          title: 'Multi-City Operations',
          description: 'Designed for deployment across multiple cities and regions with centralized management and localized operations.',
        },
        {
          title: 'Enterprise & Government',
          description: 'Ready for large-scale contracts with construction companies, industrial facilities, and government projects.',
        },
        {
          title: 'Compliance Ready',
          description: 'Built to meet regulatory requirements for water logistics, with full audit trails and documentation.',
        },
        {
          title: 'API Integration',
          description: 'Connect Watrly with existing systems through our documented APIs for seamless data exchange.',
        },
      ],
      stats: {
        stations: 'Water Stations',
        drivers: 'Active Drivers',
        deliveries: 'Deliveries Monthly',
        success: 'Delivery Success',
      },
    },
    footer: {
      ctaTitle: 'Ready to modernize your water logistics?',
      ctaSubtitle: 'Whether you need a single delivery or want to integrate Watrly into your operations, we are here to help.',
      requestWater: 'Request Water',
      contactSales: 'Contact Sales',
      brandStatement: 'Water infrastructure for Saudi Arabia. Connecting customers with licensed water stations through intelligent dispatch and real-time tracking.',
      platform: 'Platform',
      company: 'Company',
      legal: 'Legal',
      aboutUs: 'About Us',
      careers: 'Careers',
      contact: 'Contact',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      copyright: '2026 Watrly. All rights reserved.',
      tagline: 'Water Infrastructure for Saudi Arabia',
    },
  },
  ar: {
    nav: {
      howItWorks: 'آلية العمل',
      whyWatrly: 'مميزاتنا',
      platform: 'المنصة',
      requestWater: 'اطلب الآن',
    },
    hero: {
      badge: 'منصة توصيل المياه الذكية',
      title: 'مياهك تصلك',
      titleHighlight: 'بضغطة زر',
      titleEnd: 'في المملكة العربية السعودية',
      subtitle: 'نربطك بمحطات المياه المرخصة مع تتبع لحظي لكل صهريج. سعات من 18 إلى 32 طن، توصيل موثوق وسريع.',
      cta: 'اطلب الآن',
      ctaSecondary: 'تعرف على الخدمة',
      statCapacity: 'طن سعة',
      statTracking: 'تتبع مباشر',
      statLive: 'لحظي',
      statOperations: 'على مدار الساعة',
      scroll: 'اكتشف المزيد',
    },
    problem: {
      badge: 'المشكلة',
      title: 'قطاع توصيل المياه يحتاج إلى تطوير',
      subtitle: 'الطرق التقليدية لطلب المياه مرهقة وغير فعالة. العملاء يعانون من صعوبة الحصول على خدمة موثوقة، وأصحاب المحطات يواجهون تحديات في إدارة عملياتهم.',
      problems: [
        {
          title: 'البحث المتعب',
          description: 'وقت طويل يُهدر في البحث عن محطات موثوقة والاتصال بأرقام متعددة للحصول على أفضل سعر وموعد مناسب.',
        },
        {
          title: 'غياب التتبع',
          description: 'لا طريقة لمعرفة متى سيصل الصهريج. انتظار بلا معلومات واضحة عن موقع التوصيل أو الوقت المتوقع.',
        },
        {
          title: 'تسعير غير موحد',
          description: 'أسعار مختلفة من محطة لأخرى بدون معيار واضح، مما يسبب ارتباك وعدم ثقة في التعاملات.',
        },
        {
          title: 'إدارة معقدة',
          description: 'أصحاب المحطات يكافحون لتنظيم السائقين وتتبع الطلبات والحفاظ على مستوى خدمة متميز.',
        },
      ],
      bottomBadge: 'وترلي صُممت لحل كل هذه التحديات',
    },
    howItWorks: {
      badge: 'آلية العمل',
      title: 'أربع خطوات فقط لتصلك المياه',
      subtitle: 'عملية سهلة وسريعة من الطلب حتى التوصيل. كل خطوة مصممة لتوفير أفضل تجربة ممكنة.',
      steps: [
        {
          number: '١',
          title: 'حدد الكمية',
          description: 'اختر حجم الصهريج المناسب لاحتياجك. 18 طن للمنازل، أو 32 طن للمشاريع والمواقع الكبيرة.',
        },
        {
          number: '٢',
          title: 'نجد لك الأقرب',
          description: 'النظام يحدد تلقائياً أقرب محطة مياه متاحة ويُرسل طلبك إليها فوراً.',
        },
        {
          number: '٣',
          title: 'السائق في الطريق',
          description: 'يتم تعيين سائق معتمد وتصلك بياناته ومعلومات الصهريج على تطبيقك مباشرة.',
        },
        {
          number: '٤',
          title: 'تتبع وصول طلبك',
          description: 'شاهد موقع الصهريج لحظة بلحظة على الخريطة حتى يصل إلى باب منزلك.',
        },
      ],
    },
    whyWatrly: {
      badge: 'لماذا وترلي؟',
      title: 'تقنية متطورة لخدمة موثوقة',
      subtitle: 'كل ميزة في المنصة صُممت بعناية لحل مشكلة حقيقية. نركز على ما يهم: سرعة، موثوقية، وشفافية كاملة.',
      stationNetwork: 'شبكة محطات وترلي',
      stationDesc: 'محطات مياه معتمدة ومرخصة في جميع أنحاء المملكة',
      features: [
        {
          title: 'تتبع لحظي بالخريطة',
          description: 'تابع صهريجك من لحظة خروجه من المحطة حتى وصوله إليك. موقع دقيق ووقت وصول محدث.',
        },
        {
          title: 'توزيع ذكي للطلبات',
          description: 'النظام يختار أقرب محطة متاحة ويوجه طلبك إليها تلقائياً لضمان أسرع توصيل.',
        },
        {
          title: 'سائقون معتمدون',
          description: 'جميع السائقين موثقون ومقيّمون. تقييمات العملاء وسجل الأداء متاح للاطلاع.',
        },
        {
          title: 'لوحة تحكم متكاملة',
          description: 'إدارة كاملة للطلبات والسائقين والأسطول مع تقارير وإحصائيات تفصيلية.',
        },
        {
          title: 'خيارات دفع متعددة',
          description: 'ادفع بالطريقة التي تناسبك: Apple Pay، بطاقات الائتمان، أو الدفع عند الاستلام.',
        },
        {
          title: 'حلول للشركات',
          description: 'باقات خاصة للشركات والمشاريع مع فواتير شهرية وإدارة حسابات مخصصة.',
        },
      ],
    },
    platform: {
      badge: 'المنصة',
      title: 'تجربة مصممة للجميع',
      subtitle: 'واجهات سهلة ومميزة لكل مستخدم. سواء كنت عميل أو صاحب محطة أو مدير تشغيل.',
      tabs: {
        customer: 'تطبيق العميل',
        admin: 'لوحة التحكم',
        tracking: 'التتبع المباشر',
      },
      customer: {
        title: 'اطلب مياهك بسهولة',
        description: 'تطبيق بسيط وسريع. اختر الكمية، حدد موقعك، وتابع طلبك لحظة بلحظة. كل شيء في متناول يدك.',
        features: ['اختيار حجم الصهريج', 'تحديد المحطة الأقرب', 'تتبع مباشر بالخريطة', 'دفع إلكتروني آمن'],
      },
      admin: {
        title: 'تحكم كامل بالعمليات',
        description: 'لوحة تحكم شاملة لأصحاب المحطات. تابع كل الطلبات، أدر السائقين، واطلع على أداء محطتك.',
        features: ['إدارة الطلبات', 'توزيع السائقين', 'متابعة الأسطول', 'تقارير الأداء'],
      },
      tracking: {
        title: 'تتبع كل خطوة',
        description: 'اعرف وضع طلبك في أي لحظة. من تأكيد الطلب حتى وصول الصهريج لموقعك بتحديثات فورية.',
        features: ['موقع الصهريج بالخريطة', 'إشعارات فورية', 'وقت الوصول المتوقع', 'تأكيد التسليم'],
      },
      mockup: {
        welcomeBack: 'أهلاً بك',
        activeDelivery: 'طلب جاري',
        tonTanker: 'طن',
        enRoute: 'في الطريق',
        minAway: 'دقيقة',
        selectCapacity: 'اختر الحجم',
        ton: 'طن',
        deliveryLocation: 'عنوان التوصيل',
        mapPreview: 'الخريطة',
        operationsDashboard: 'لوحة التحكم',
        realtimeOverview: 'المتابعة اللحظية',
        live: 'مباشر',
        onTimeRate: 'نسبة الالتزام',
        driversOnline: 'سائق متصل',
        activeOrders: 'طلب نشط',
        recentOrders: 'آخر الطلبات',
        delivering: 'قيد التوصيل',
        filling: 'قيد التعبئة',
        assigned: 'تم التعيين',
        min: 'د',
        deliveryStatus: 'حالة الطلب',
        onTheWay: 'في الطريق إليك',
        orderPlaced: 'تم استلام الطلب',
        driverAssigned: 'تم تعيين السائق',
        fillingComplete: 'اكتملت التعبئة',
      },
    },
    scale: {
      badge: 'جاهزون للنمو',
      title: 'منصة تنمو مع طموحاتك',
      subtitle: 'وترلي ليست مجرد تطبيق توصيل. إنها بنية تحتية متكاملة تدعم نمو أعمالك من توصيلات فردية إلى عقود كبرى مع الشركات والجهات الحكومية.',
      capabilities: [
        {
          title: 'تغطية واسعة',
          description: 'نعمل في مختلف مدن ومناطق المملكة مع إدارة مركزية وتشغيل محلي فعّال.',
        },
        {
          title: 'شراكات مؤسسية',
          description: 'حلول مخصصة لشركات المقاولات والمشاريع الصناعية والجهات الحكومية بعقود مرنة.',
        },
        {
          title: 'معايير عالية',
          description: 'نلتزم بجميع الاشتراطات والمعايير الرسمية مع توثيق كامل لكل عملية.',
        },
        {
          title: 'تكامل تقني',
          description: 'ربط سهل مع أنظمتك الحالية عبر واجهات برمجية موثقة ودعم فني متواصل.',
        },
      ],
      stats: {
        stations: 'محطة معتمدة',
        drivers: 'سائق نشط',
        deliveries: 'توصيلة شهرياً',
        success: 'نسبة النجاح',
      },
    },
    footer: {
      ctaTitle: 'جاهز لتجربة الفرق؟',
      ctaSubtitle: 'سواء احتجت توصيلة واحدة أو أردت شراكة دائمة، فريقنا جاهز لخدمتك.',
      requestWater: 'اطلب الآن',
      contactSales: 'تواصل معنا',
      brandStatement: 'منصة توصيل المياه الأولى في المملكة. نربط العملاء بمحطات المياه المعتمدة مع تتبع لحظي وخدمة موثوقة.',
      platform: 'المنصة',
      company: 'الشركة',
      legal: 'القانونية',
      aboutUs: 'من نحن',
      careers: 'انضم لفريقنا',
      contact: 'تواصل معنا',
      privacy: 'الخصوصية',
      terms: 'الشروط والأحكام',
      copyright: '٢٠٢٦ وترلي. جميع الحقوق محفوظة.',
      tagline: 'منصة توصيل المياه في المملكة',
    },
  },
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('watrly-language') || 'ar';
    }
    return 'ar';
  });

  useEffect(() => {
    localStorage.setItem('watrly-language', language);
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === 'en' ? 'ar' : 'en'));
  };

  const t = translations[language];

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
